/* eslint-disable react-hooks/set-state-in-effect */
import { useCallback, useEffect, useState } from 'react'
import { kanbify } from '../lib/kanbifyClient'

/* ========== COMMENTS ========== */
export function useComments(cardId) {
    const [comments, setComments] = useState([])
    const [commentReactions, setCommentReactions] = useState([])

    const fetchReactions = useCallback(async (commentList) => {
        const ids = (commentList || []).map(c => c.id)
        if (ids.length === 0) { setCommentReactions([]); return }
        const { data } = await kanbify
            .from('comment_reactions')
            .select('*')
            .in('comment_id', ids)
        setCommentReactions(data || [])
    }, [])

    const fetch = useCallback(async () => {
        if (!cardId) return
        const { data } = await kanbify
            .from('comments')
            .select('*, profiles:user_id ( id, full_name, avatar_url )')
            .eq('card_id', cardId)
            .order('created_at', { ascending: false })
        const list = data || []
        setComments(list)
        fetchReactions(list)
    }, [cardId, fetchReactions])

    useEffect(() => { fetch() }, [fetch])

    useEffect(() => {
        if (!cardId) return
        const channel = kanbify.channel(`comments:${cardId}`)
            .on('postgres_changes', { event: '*', schema: 'public', table: 'comments', filter: `card_id=eq.${cardId}` }, () => fetch())
            .subscribe()
        return () => kanbify.removeChannel(channel)
    }, [cardId, fetch])

    const add = async (userId, text, parentCommentId = null) => {
        const { data: newComment, error } = await kanbify
            .from('comments')
            .insert([{
                card_id: cardId,
                user_id: userId,
                text: text,
                parent_comment_id: parentCommentId,
            }])
            .select()
            .single()

        if (error || !newComment) return null

        const { data } = await kanbify
            .from('comments')
            .select('*, profiles:user_id ( id, full_name, avatar_url )')
            .eq('id', newComment.id)
            .single()
        if (data) setComments(p => [data, ...p])
        return data
    }

    const toggleCommentReaction = async (commentId, userId, emoji) => {
        const { data: existing } = await kanbify
            .from('comment_reactions')
            .select('id')
            .eq('comment_id', commentId)
            .eq('user_id', userId)
            .eq('emoji', emoji)
            .maybeSingle()
        if (existing) {
            await kanbify.from('comment_reactions').delete().eq('id', existing.id)
        } else {
            await kanbify.from('comment_reactions').insert([{ comment_id: commentId, user_id: userId, emoji }])
        }
        fetchReactions(comments)
        return { success: true }
    }

    const update = async (commentId, text) => {
        const { error } = await kanbify
            .from('comments')
            .update({ text })
            .eq('id', commentId)
        if (error) return { success: false, error: error.message }
        setComments(prev => prev.map(c => c.id === commentId ? { ...c, text } : c))
        return { success: true }
    }

    const remove = async (commentId) => {
        // Re-parent replies to top-level (avoid orphaned threads) + drop reactions.
        await kanbify.from('comments').update({ parent_comment_id: null }).eq('parent_comment_id', commentId)
        await kanbify.from('comment_reactions').delete().eq('comment_id', commentId)
        const { error } = await kanbify
            .from('comments')
            .delete()
            .eq('id', commentId)
        if (error) return { success: false, error: error.message }
        setComments(prev => prev.map(c => (c.parent_comment_id === commentId ? { ...c, parent_comment_id: null } : c)).filter(c => c.id !== commentId))
        return { success: true }
    }

    return { comments, commentReactions, fetch, add, update, remove, toggleCommentReaction }
}
